"use client";

import { AuthUserEntity } from "@yardzen/api-dtos";
import { isYardzenHost } from "@yardzen/js-util";
import { getCurrentUser, getV2Token } from "@yardzen/rest-api-client";
import Cookies from "js-cookie";
import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { v2TokenCookieName, v2TokenValue } from "./utils";

interface ContextProps {
  isAuthenticated: boolean;
  authUser?: AuthUserEntity;
}

interface Props {
  children: ReactNode;
}

const V2AuthContext = createContext<ContextProps>({
  isAuthenticated: false,
  authUser: undefined,
});

export function V2AuthProvider({ children }: Props) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [authUser, setAuthUser] = useState<AuthUserEntity | undefined>(
    undefined,
  );
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const doAuthCheck = async () => {
      let v2Token = v2TokenValue();

      if (v2Token.length === 0) {
        setLoading(true);
        try {
          v2Token = (await getV2Token()).data.v2Token;
          if (!v2Token) {
            throw new Error("No V2 token found");
          }
        } catch (error) {
          console.error("Error fetching V2 token:", error);
          const currentPath = window.location.pathname + window.location.search;
          const loginUrl = `/login?callbackUrl=${encodeURIComponent(currentPath)}`;
          window.location.href = loginUrl;
          return;
        }

        // Match the server-side cookie domain so signOut's delete (which uses
        // AUTH_COOKIE_DOMAIN=.yardzen.com on staging/prod) actually targets
        // this cookie. Without this it gets written host-only and survives
        // signOut, leaving stale auth.
        const cookieDomain = isYardzenHost(window.location.hostname)
          ? ".yardzen.com"
          : undefined;
        Cookies.set(v2TokenCookieName, v2Token, { domain: cookieDomain });

        const { data: user } = await getCurrentUser();
        setAuthUser(user);
        setIsAuthenticated(true);
        setLoading(false);
      } else {
        setIsAuthenticated(true);
        setLoading(false);
      }
    };

    doAuthCheck();
  }, []);

  return (
    <V2AuthContext.Provider
      value={{
        isAuthenticated,
        authUser,
      }}
    >
      {loading ? null : children}
    </V2AuthContext.Provider>
  );
}

export function useV2Auth() {
  return useContext(V2AuthContext);
}
